import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Box, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, VolumeUp } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface PronunciationEntry {
  id: number;
  tenant_id: number;
  word: string;
  pronunciation: string;
}

export default function PronunciationPage() {
  const { tenantId, role } = useAuth();
  const [entries, setEntries] = useState<PronunciationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [word, setWord] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [previewLoading, setPreviewLoading] = useState<number | null>(null);

  const isAdmin = role === 'admin';

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/pronunciation`);
      setEntries(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dictionary');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setWord(''); setPronunciation(''); setDialogOpen(true); };

  const openEdit = (e: PronunciationEntry) => {
    setEditingId(e.id);
    setWord(e.word);
    setPronunciation(e.pronunciation);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    const payload = { word, pronunciation };
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/pronunciation/${editingId}`, payload);
      } else {
        await api.post(`/api/tenants/${tenantId}/pronunciation`, payload);
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/pronunciation/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handlePreview = async (text: string, entryId?: number) => {
    if (!tenantId) return;
    setPreviewLoading(entryId ?? -1);
    setError(null);
    try {
      const res = await api.post(
        `/api/tenants/${tenantId}/pronunciation/preview-tts`,
        { text, use_dictionary: true },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      audio.onerror = () => {
        setError('Audio playback failed');
        URL.revokeObjectURL(audioUrl);
      };
      await audio.play();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(`TTS preview failed: ${detail}`);
      } else if (err.name === 'NotAllowedError') {
        setError('Browser blocked audio playback. Click the page first, then try again.');
      } else {
        setError(`TTS preview failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setPreviewLoading(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Pronunciation Dictionary</Typography>
        {isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Entry</Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Custom word pronunciations for TTS. Changes are automatically pushed to all connected station and radio agents.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Word / Abbreviation</TableCell>
              <TableCell>Pronunciation</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} align="center">Loading...</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">No custom pronunciations</TableCell></TableRow>
            ) : entries.map(e => (
              <TableRow key={e.id}>
                <TableCell><strong>{e.word}</strong></TableCell>
                <TableCell>{e.pronunciation}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Preview TTS">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePreview(e.pronunciation, e.id)}
                      disabled={previewLoading === e.id}
                    >
                      <VolumeUp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <>
                      <IconButton size="small" onClick={() => openEdit(e)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(e.id)}><Delete fontSize="small" /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Pronunciation' : 'Add Pronunciation'}</DialogTitle>
        <DialogContent>
          <TextField label="Word / Abbreviation" fullWidth margin="dense" value={word} onChange={e => setWord(e.target.value)} required
            helperText="The word as it appears in dispatch text (e.g., Hwy, Cty Rd, Auxvasse)" />
          <TextField label="Pronunciation" fullWidth margin="dense" value={pronunciation} onChange={e => setPronunciation(e.target.value)} required
            helperText="How it should be spoken (e.g., Highway, County Road, aw-VOSS)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePreview(pronunciation || word)} disabled={!pronunciation && !word} startIcon={<VolumeUp />}>
            Preview
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
